<script setup lang="ts">
import type { SupplierOption, SupplierValue } from '~/utils/receipt-model/supplier'
import IconExpandLess from '~icons/material-symbols/expand-less'
import IconExpandMore from '~icons/material-symbols/expand-more'
import IconProgressActivity from '~icons/material-symbols/progress-activity'
import IconSearch from '~icons/material-symbols/search'

type GroupKey = 'partners' | 'others'

interface GroupDefinition {
  key: GroupKey
  isPartner: boolean
  labelKey: string
}

interface OptionItem {
  id: string
  index: number
  option: SupplierOption
}

interface OptionGroup {
  key: GroupKey
  labelId: string
  label: string
  items: OptionItem[]
}

const props = withDefaults(defineProps<{
  modelValue?: SupplierValue | null
  options: SupplierOption[]
  label?: string
  placeholder?: string
  disabled?: boolean
  error?: string | null
  id?: string
  name?: string
  loading?: boolean
}>(), {
  modelValue: null,
  disabled: false,
  loading: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: SupplierValue]
  'blur': [event: FocusEvent]
}>()

const GROUPS: GroupDefinition[] = [
  { key: 'partners', isPartner: true, labelKey: 'receipt-model.supplier_select.group_partners' },
  { key: 'others', isPartner: false, labelKey: 'receipt-model.supplier_select.group_others' },
]

const { t } = useI18n()

const fallbackId = useId()
const baseId = computed(() => props.id || `supplier-select-${fallbackId}`)
const labelId = computed(() => `${baseId.value}-label`)
const listboxId = computed(() => `${baseId.value}-listbox`)
const searchId = computed(() => `${baseId.value}-search`)
const errorId = computed(() => `${baseId.value}-error`)

const rootRef = ref<HTMLElement | null>(null)
const triggerRef = ref<HTMLButtonElement | null>(null)
const searchInputRef = ref<HTMLInputElement | null>(null)
const listboxRef = ref<HTMLElement | null>(null)
const optionElements = new Map<number, HTMLElement>()

const isOpen = ref(false)
const searchQuery = ref('')
const activeIndex = ref(-1)
let focusRestoreTimer: ReturnType<typeof setTimeout> | undefined

const placeholderText = computed(() => props.placeholder ?? t('receipt-model.supplier_select.placeholder'))
const hasError = computed(() => Boolean(props.error))

const selectedValue = computed(() => {
  const value = props.modelValue?.value ?? 0
  return value > 0 ? value : null
})

const displayName = computed(() => {
  if (selectedValue.value === null)
    return ''

  const fromOptions = props.options.find(option => option.value === selectedValue.value)?.name
  return fromOptions || props.modelValue?.name || ''
})

const hasValue = computed(() => displayName.value !== '')

const searchableOptions = computed(() => props.options.map(option => ({
  option,
  searchKey: normalizeSearchText(option.name),
})))

const optionGroups = computed<OptionGroup[]>(() => {
  const term = normalizeSearchText(searchQuery.value)
  const matches = term
    ? searchableOptions.value.filter(entry => entry.searchKey.includes(term)).map(entry => entry.option)
    : props.options

  let index = 0
  return GROUPS
    .map(group => ({ group, options: matches.filter(option => Boolean(option.isPartner) === group.isPartner) }))
    .filter(({ options }) => options.length > 0)
    .map(({ group, options }) => ({
      key: group.key,
      labelId: `${baseId.value}-group-${group.key}`,
      label: t(group.labelKey),
      items: options.map(option => ({ id: getOptionId(option.value), index: index++, option })),
    }))
})

const flatItems = computed(() => optionGroups.value.flatMap(group => group.items))
const hasResults = computed(() => flatItems.value.length > 0)
const activeItem = computed(() => flatItems.value[activeIndex.value] ?? null)

const statusMessage = computed(() => {
  if (!isOpen.value || hasResults.value)
    return ''

  return props.loading
    ? t('receipt-model.supplier_select.loading')
    : t('receipt-model.supplier_select.no_results')
})

function normalizeSearchText(text: string) {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim()
}

function getOptionId(value: number) {
  return `${baseId.value}-option-${value}`
}

function isSelected(option: SupplierOption) {
  return option.value === selectedValue.value
}

function isPrintableKey(event: KeyboardEvent) {
  return event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey
}

function setOptionElement(value: number, element: unknown) {
  if (element instanceof HTMLElement)
    optionElements.set(value, element)
  else
    optionElements.delete(value)
}

function scrollActiveIntoView() {
  const item = activeItem.value
  if (!item)
    return

  optionElements.get(item.option.value)?.scrollIntoView({ block: 'nearest' })
}

function setActive(index: number) {
  if (!hasResults.value)
    return

  activeIndex.value = Math.min(Math.max(index, 0), flatItems.value.length - 1)
  scrollActiveIntoView()
}

function open() {
  if (props.disabled || isOpen.value)
    return

  isOpen.value = true
  activeIndex.value = flatItems.value.findIndex(item => isSelected(item.option))
  nextTick(() => {
    searchInputRef.value?.focus()
    scrollActiveIntoView()
  })
}

function close({ restoreFocus = false }: { restoreFocus?: boolean } = {}) {
  isOpen.value = false
  searchQuery.value = ''
  activeIndex.value = -1

  if (restoreFocus)
    triggerRef.value?.focus()
}

function toggle() {
  if (isOpen.value)
    close({ restoreFocus: true })
  else
    open()
}

function select(option: SupplierOption) {
  if (option.value !== selectedValue.value)
    emit('update:modelValue', { name: option.name, value: option.value })

  close({ restoreFocus: true })
}

function focusSearch() {
  activeIndex.value = -1
  searchInputRef.value?.focus()
}

function focusListbox(index: number) {
  if (!hasResults.value)
    return

  setActive(index)
  listboxRef.value?.focus()
}

function closeFromKeyboard(event: KeyboardEvent) {
  event.preventDefault()
  // Keeps Esc from also closing a modal/overlay around the selector.
  event.stopPropagation()
  close({ restoreFocus: true })
}

function onTriggerKeydown(event: KeyboardEvent) {
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault()
    if (!isOpen.value)
      open()
    else if (event.key === 'ArrowDown')
      focusListbox(activeIndex.value >= 0 ? activeIndex.value : 0)
    else
      focusListbox(flatItems.value.length - 1)
  }
  else if (event.key === 'Escape' && isOpen.value) {
    closeFromKeyboard(event)
  }
}

function onSearchKeydown(event: KeyboardEvent) {
  if (event.isComposing)
    return

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      focusListbox(activeIndex.value >= 0 ? activeIndex.value : 0)
      break
    case 'ArrowUp':
      event.preventDefault()
      focusListbox(flatItems.value.length - 1)
      break
    case 'Enter':
      // Also blocks the implicit submit of the parent <form>.
      event.preventDefault()
      if (activeItem.value)
        select(activeItem.value.option)
      break
    case 'Escape':
      closeFromKeyboard(event)
      break
  }
}

function onListboxKeydown(event: KeyboardEvent) {
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      setActive(activeIndex.value + 1)
      break
    case 'ArrowUp':
      event.preventDefault()
      if (activeIndex.value <= 0)
        focusSearch()
      else
        setActive(activeIndex.value - 1)
      break
    case 'Home':
      event.preventDefault()
      setActive(0)
      break
    case 'End':
      event.preventDefault()
      setActive(flatItems.value.length - 1)
      break
    case 'Enter':
    case ' ':
      event.preventDefault()
      if (activeItem.value)
        select(activeItem.value.option)
      break
    case 'Escape':
      closeFromKeyboard(event)
      break
    default:
      // Type-to-search: focus moves before the key is handled, so the character lands in the search input.
      if (isPrintableKey(event))
        focusSearch()
  }
}

function onTriggerMousedown(event: MouseEvent) {
  if (isOpen.value)
    event.preventDefault()
}

function onPanelMousedown(event: MouseEvent) {
  // Clicks inside the panel must not blur the search input (it would emit a spurious `blur` to the form).
  if (event.target !== searchInputRef.value)
    event.preventDefault()
}

// Capture phase: closing on outside click doesn't depend on other components letting the event propagate.
function onDocumentPointerDown(event: PointerEvent) {
  const root = rootRef.value
  if (!root || (event.target instanceof Node && root.contains(event.target)))
    return

  const hadFocusInside = root.contains(document.activeElement)
  close()
  if (hadFocusInside)
    restoreFocusIfLost()
}

// Deferred until the browser moves focus for this click: only restores focus that was lost.
function restoreFocusIfLost() {
  clearTimeout(focusRestoreTimer)
  focusRestoreTimer = setTimeout(() => {
    const activeElement = document.activeElement
    if (!activeElement || activeElement === document.body)
      triggerRef.value?.focus()
  })
}

function onRootFocusout(event: FocusEvent) {
  const next = event.relatedTarget
  if (next instanceof Node && rootRef.value?.contains(next))
    return

  if (isOpen.value && next)
    close()
  emit('blur', event)
}

function removeDocumentListener() {
  document.removeEventListener('pointerdown', onDocumentPointerDown, true)
}

watch(isOpen, (value) => {
  if (value)
    document.addEventListener('pointerdown', onDocumentPointerDown, true)
  else
    removeDocumentListener()
})

watch(searchQuery, (query) => {
  activeIndex.value = normalizeSearchText(query) !== '' && hasResults.value ? 0 : -1
})

watch(() => flatItems.value.length, (length) => {
  if (activeIndex.value >= length)
    activeIndex.value = length - 1
})

watch(() => props.disabled, (disabled) => {
  if (disabled && isOpen.value)
    close()
})

onBeforeUnmount(() => {
  removeDocumentListener()
  clearTimeout(focusRestoreTimer)
})
</script>

<template>
  <div
    ref="rootRef"
    class="supplier-select"
    :data-testid="`supplier-select-${baseId}`"
    @focusout="onRootFocusout"
  >
    <label
      v-if="label"
      :id="labelId"
      :for="baseId"
      class="supplier-select__label"
    >
      {{ label }}
    </label>

    <div class="supplier-select__control">
      <button
        :id="baseId"
        ref="triggerRef"
        type="button"
        class="supplier-select__trigger"
        :class="{ 'supplier-select__trigger--error': hasError }"
        :name="name"
        :disabled="disabled"
        aria-haspopup="listbox"
        :aria-expanded="isOpen"
        :aria-controls="listboxId"
        :aria-labelledby="label ? `${labelId} ${baseId}` : undefined"
        :aria-invalid="hasError"
        :aria-describedby="hasError ? errorId : undefined"
        :aria-busy="loading || undefined"
        data-testid="supplier-select-trigger"
        @mousedown="onTriggerMousedown"
        @click="toggle"
        @keydown="onTriggerKeydown"
      >
        <span
          class="supplier-select__value"
          :class="{ 'supplier-select__value--placeholder': !hasValue }"
        >
          {{ hasValue ? displayName : placeholderText }}
        </span>
        <IconProgressActivity
          v-if="loading"
          class="supplier-select__indicator supplier-select__indicator--loading"
          aria-hidden="true"
        />
        <IconExpandLess
          v-else-if="isOpen"
          class="supplier-select__indicator"
          aria-hidden="true"
        />
        <IconExpandMore
          v-else
          class="supplier-select__indicator"
          aria-hidden="true"
        />
      </button>

      <div
        v-show="isOpen"
        class="supplier-select__panel"
        data-testid="supplier-select-panel"
        @mousedown="onPanelMousedown"
      >
        <div class="supplier-select__search">
          <!-- `.stop`: search typing must not reach the vee-validate onInput/onChange that fall through from v-bind. -->
          <input
            :id="searchId"
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            role="combobox"
            class="supplier-select__search-input"
            autocomplete="off"
            spellcheck="false"
            :placeholder="t('receipt-model.supplier_select.search_placeholder')"
            :aria-label="t('receipt-model.supplier_select.search_label')"
            aria-autocomplete="list"
            :aria-expanded="isOpen && hasResults"
            :aria-controls="listboxId"
            :aria-activedescendant="activeItem?.id"
            data-testid="supplier-select-search"
            @keydown="onSearchKeydown"
            @input.stop
            @change.stop
          >
          <IconSearch class="supplier-select__search-icon" aria-hidden="true" />
        </div>

        <div
          v-show="hasResults"
          :id="listboxId"
          ref="listboxRef"
          role="listbox"
          tabindex="-1"
          class="supplier-select__listbox"
          :aria-labelledby="label ? labelId : undefined"
          :aria-label="label ? undefined : t('receipt-model.supplier_select.listbox_label')"
          :aria-activedescendant="activeItem?.id"
          :aria-busy="loading || undefined"
          data-testid="supplier-select-listbox"
          @keydown="onListboxKeydown"
        >
          <ul
            v-for="group in optionGroups"
            :key="group.key"
            role="group"
            class="supplier-select__group"
            :aria-labelledby="group.labelId"
            :data-testid="`supplier-select-group-${group.key}`"
          >
            <li
              :id="group.labelId"
              role="presentation"
              class="supplier-select__group-label"
            >
              {{ group.label }}
            </li>
            <li
              v-for="item in group.items"
              :id="item.id"
              :key="item.option.value"
              :ref="element => setOptionElement(item.option.value, element)"
              role="option"
              class="supplier-select__option"
              :class="{
                'supplier-select__option--active': item.index === activeIndex,
                'supplier-select__option--selected': isSelected(item.option),
              }"
              :aria-selected="isSelected(item.option)"
              :data-testid="`supplier-select-option-${item.option.value}`"
              @click="select(item.option)"
              @mousemove="activeIndex = item.index"
            >
              {{ item.option.name }}
            </li>
          </ul>
        </div>

        <div role="status">
          <p
            v-if="statusMessage"
            class="supplier-select__status !mx-0"
            data-testid="supplier-select-status"
          >
            {{ statusMessage }}
          </p>
        </div>
      </div>
    </div>

    <p
      v-if="hasError"
      :id="errorId"
      class="supplier-select__message !mx-0"
      data-testid="supplier-select-error"
    >
      {{ error }}
    </p>
  </div>
</template>

<style lang="scss" scoped>
// Values without a token: trigger right padding 44px, dropdown max height 320px, group label 11px.
.supplier-select {
  @apply flex flex-col;

  &__label {
    @apply mb-quark text-left fonts-body-medium-bold text-neutral-low-dark;
  }

  &__control {
    @apply relative;
  }

  &__trigger {
    @apply relative flex items-center w-full h-md pl-4xs pr-[44px];
    @apply rounded-md border-a-xs border-solid border-neutral-high-medium bg-neutral-high-pure;
    @apply text-left cursor-pointer outline-none transition-colors;

    &:focus-visible {
      @apply border-neutral-low-dark;
    }

    &--error,
    &--error:focus-visible {
      @apply border-feedback-negative-pure;
    }

    &:disabled {
      @apply bg-neutral-high-light cursor-not-allowed;
    }
  }

  &__value {
    @apply block flex-1 min-w-0 truncate fonts-body-large-regular text-neutral-low-medium;

    &--placeholder {
      @apply text-neutral-low-light;
    }
  }

  &__indicator {
    @apply absolute inset-y-0 right-4xs my-auto w-2xs h-2xs text-neutral-low-light pointer-events-none;

    &--loading {
      @apply animate-spin;
    }
  }

  &__trigger:disabled &__value,
  &__trigger:disabled &__indicator {
    @apply text-neutral-high-dark;
  }

  // z-10: above the step numbers (z-0), below the layout's sticky header (z-19).
  &__panel {
    @apply absolute left-0 right-0 top-full z-10 mt-quark;
    @apply flex flex-col max-h-[320px] overflow-hidden;
    @apply rounded-md border-a-xs border-solid border-neutral-high-medium bg-neutral-high-pure shadow-moderate;
  }

  &__search {
    @apply relative flex-shrink-0 p-micro border-b-xs border-solid border-neutral-high-medium;
  }

  &__search-input {
    @apply w-full h-sm pl-4xs pr-sm;
    @apply rounded-md border-a-xs border-solid border-neutral-high-medium bg-neutral-high-pure;
    @apply fonts-body-medium-regular text-neutral-low-medium outline-none transition-colors;

    &::placeholder {
      @apply fonts-body-medium-regular text-neutral-low-light;
    }

    &:focus {
      @apply border-neutral-low-medium;
    }
  }

  &__search-icon {
    @apply absolute inset-y-0 right-2xs my-auto w-4xs h-4xs text-neutral-low-light pointer-events-none;
  }

  &__listbox {
    @apply overflow-y-auto outline-none;
  }

  &__group-label {
    @apply mt-quark px-4xs py-nano;
    @apply font-bold text-[11px] leading-xs uppercase tracking-md text-neutral-low-medium;
  }

  &__option {
    @apply px-4xs py-micro cursor-pointer transition-colors;
    @apply fonts-body-medium-regular text-neutral-low-medium;

    &:hover,
    &--active {
      @apply bg-neutral-high-light;
    }

    &--selected {
      @apply font-bold text-neutral-low-dark;
    }
  }

  &__status {
    @apply px-4xs py-micro fonts-body-medium-regular text-neutral-low-light;
  }

  &__message {
    @apply mt-quark text-left fonts-body-small-regular text-feedback-negative-pure;
  }
}
</style>
